import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Box, Button, Chip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { LogicalNode, QueryNode } from "modules/record/record-producer/types/query.types";
import {
    updateNode,
    addConditionOrGroup,
    addExistsAfter
} from "./utils/ast.utils";
import { ConditionRow } from "./ConditionRow";
import type { ConditionPresentation } from "./ConditionRow";
import { ExistsRow } from "./ExistsRow";

import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";

interface Props {
    node: LogicalNode;
    columns: SysColumn[];
    allColumns?: SysColumn[];
    tables?: SysTable[];
    maxReferenceDepth?: number;
    onChange: (node: LogicalNode) => void;
    onRemove: (id: string) => void;
    isNested?: boolean;
    formFields?: { name: string; label: string }[];
    flatFields?: boolean;
    compact?: boolean;
    addConditionLabel?: string;
    andLabel?: string;
    orLabel?: string;
    fieldAriaLabel?: string;
    presentation?: ConditionPresentation;
    guided?: boolean;
    allowedOperators?: string[];
    allowExists?: boolean;
}

const LogicalSeparator = ({
    operator,
    isNested,
    compact,
    andLabel,
    orLabel,
    onToggle,
}: {
    operator: "and" | "or";
    isNested?: boolean;
    compact?: boolean;
    andLabel?: string;
    orLabel?: string;
    onToggle?: () => void;
}) => {
    const { t } = useTranslation();
    const label = operator === "and" ? andLabel || String(t("AND")) : orLabel || String(t("OR"));
    const color = operator === "and"
        ? (isNested ? "#1976d2" : "primary.main")
        : (isNested ? "#9c27b0" : "secondary.main");

    if (compact) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 0.25 }}>
                <Chip
                    size="small"
                    clickable
                    onClick={onToggle}
                    label={label}
                    sx={{ fontSize: 11, fontWeight: 700, height: 24 }}
                />
            </Box>
        );
    }

    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={{
                width: "100%",
                minHeight: 28,
                my: 0.25,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 48,
                    height: 28,
                    position: "relative",
                    flexShrink: 0,
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        left: "50%",
                        top: 0,
                        bottom: 0,
                        width: isNested ? 3 : 2,
                        backgroundColor: color,
                    },
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        bgcolor: color,
                        color: "white",
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.5,
                        fontWeight: "bold",
                        fontSize: "0.7rem",
                        zIndex: 1,
                        boxShadow: isNested ? 1 : 0,
                    }}
                >
                    {label}
                </Typography>
            </Box>
        </Stack>
    );
};

export const LogicalGroup = ({
    node,
    columns,
    allColumns,
    tables,
    maxReferenceDepth,
    onChange,
    onRemove,
    isNested = false,
    formFields,
    flatFields = false,
    compact = false,
    addConditionLabel,
    andLabel,
    orLabel,
    fieldAriaLabel,
    presentation,
    guided = false,
    allowedOperators,
    allowExists = true,
}: Props) => {
    const handleChildChange = (updated: QueryNode) => {
        const updatedGroup = updateNode(node, updated);
        onChange(updatedGroup);
    };

    const handleAddLogicalGroup = (conditionId: string, operator: 'and' | 'or') => {
        const updatedNode = addConditionOrGroup(node, conditionId, operator);
        onChange(updatedNode);
    };

    const handleAddExists = (nodeId: string, operator: 'and' | 'or' = 'and') => {
        const updatedNode = addExistsAfter(node, nodeId, operator);
        onChange(updatedNode);
    };

    return (
        <Box sx={{ width: "100%", minWidth: 0 }}>
            <Stack
                spacing={1}
                sx={{
                    width: "100%",
                    minWidth: 0,
                    pl: { xs: 1, sm: 2 },
                    borderLeft: isNested
                        ? `3px solid ${node.operator === 'and' ? '#1976d2' : '#9c27b0'}`
                        : "2px solid #ddd",
                    borderRadius: isNested ? 1 : 0,
                    bgcolor: isNested ? (node.operator === 'and' ? 'rgba(25, 118, 210, 0.04)' : 'rgba(156, 39, 176, 0.04)') : 'transparent',
                    py: isNested ? 1 : 0,
                }}
            >
                {node.conditions.map((child, index) => (
                    <Fragment key={child.id}>
                        {index > 0 && (
                            <LogicalSeparator
                                operator={node.operator}
                                isNested={isNested}
                                compact={compact}
                                andLabel={andLabel}
                                orLabel={orLabel}
                                onToggle={() => onChange({
                                    ...node,
                                    operator: node.operator === "and" ? "or" : "and",
                                })}
                            />
                        )}
                        {child.type === "condition" ? (
                            <ConditionRow
                                node={child}
                                columns={columns}
                                allColumns={allColumns}
                                tables={tables}
                                maxReferenceDepth={maxReferenceDepth}
                                formFields={formFields}
                                onChange={handleChildChange}
                                onRemove={() => onRemove(child.id)}
                                onAddLogicalGroup={(operator) => handleAddLogicalGroup(child.id, operator)}
                                onAddExists={allowExists ? (operator) => handleAddExists(child.id, operator) : undefined}
                                allowedOperators={allowedOperators}
                                flatFields={flatFields}
                                compact={compact}
                                fieldAriaLabel={fieldAriaLabel}
                                presentation={presentation}
                                guided={guided}
                                conditionCount={node.conditions.length}
                            />
                        ) : child.type === "exists" ? (
                            <ExistsRow
                                node={child}
                                baseColumns={columns}
                                allColumns={allColumns}
                                tables={tables}
                                maxReferenceDepth={maxReferenceDepth}
                                formFields={formFields}
                                onChange={handleChildChange}
                                onRemove={() => onRemove(child.id)}
                                onAddLogicalGroup={(operator) => handleAddLogicalGroup(child.id, operator)}
                                onAddExists={(operator) => handleAddExists(child.id, operator)}
                                isInNestedGroup={isNested}
                            />
                        ) : (
                            <LogicalGroup
                                node={child}
                                columns={columns}
                                allColumns={allColumns}
                                tables={tables}
                                maxReferenceDepth={maxReferenceDepth}
                                formFields={formFields}
                                onChange={handleChildChange}
                                onRemove={onRemove}
                                isNested={true}
                                flatFields={flatFields}
                                compact={compact}
                                addConditionLabel={addConditionLabel}
                                andLabel={andLabel}
                                orLabel={orLabel}
                                fieldAriaLabel={fieldAriaLabel}
                                presentation={presentation}
                                guided={guided}
                                allowedOperators={allowedOperators}
                                allowExists={allowExists}
                            />
                        )}
                    </Fragment>
                ))}
            </Stack>
            {compact && node.conditions.length > 0 && (
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ mt: 1 }}
                    onClick={() => handleAddLogicalGroup(
                        node.conditions[node.conditions.length - 1].id,
                        node.operator
                    )}
                >
                    {addConditionLabel}
                </Button>
            )}
        </Box>
    );
};
